// Chase-card entity silo #2: parallel / alternate art printings only, grouped by
// rarity tier into short bounded tables. Answers "how much is an SSP alternate
// art card worth" without asking the reader (or an answer engine) to scroll a
// 250-row grid.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getPriceBoard } from "@/lib/prices.functions";
import { type PriceRow } from "@/lib/prices-shared";
import { OFFICIAL_CARDS, OFFICIAL_CARD_BY_CODE, RARITY_MEANINGS } from "@/lib/tcg-cards";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, collectionSchema, datasetSchema, snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";
import { PriceFreshness } from "@/components/PriceFreshness";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { CrossCategoryLinks } from "@/components/CrossCategoryLinks";
import { ShareButton } from "@/components/ShareButton";
import { PriceTable } from "@/routes/most-expensive-palworld-cards";
import { pageTitle } from "@/lib/seo-title";

const URL = "https://palstars.com/palworld-alternate-art-price-guide";

const SNIPPET_Q = "How much is a Palworld alternate art card worth?";
const SNIPPET_A =
  "Palworld alternate art cards are the parallel printings of a base card number — OSR (Ultra Secret Gold), SSP (Special Signature Parallel), SR/SP and the Trial Deck TSR/TSP secrets. They share the base card's name, number and rules text, so the premium is entirely scarcity: an alternate art printing typically trades at a multiple of its base version, with OSR and SSP at the top of the ladder. Because pull rates differ per set, each rarity tier has its own price band, and Pal Stars recalculates every band from validated marketplace listings on each daily sync.";

const FAQS: FaqItem[] = [
  {
    q: "What counts as a Palworld alternate art card?",
    a: "Any printing whose card number carries a parallel suffix: OSR, SSP, SR, SP, TSR or TSP. The base number (for example EBP01-001) is the standard printing; EBP01-001OSR and EBP01-001SSP are its alternate arts. All of them count towards the same 4-copy deck limit.",
  },
  {
    q: "How much is an SSP alternate art card worth?",
    a: "SSP printings sit in the top price band on this page, above SR and SP parallels and well above the base card. Each SSP printing's current market value and TCGplayer sold average is listed in the SSP table below, refreshed twice daily.",
  },
  {
    q: "Which is worth more, OSR or SSP?",
    a: "OSR (Ultra Secret Gold) is the rarest pull in a Palworld booster set, so OSR printings usually carry the highest values. SSP follows closely and can overtake a specific OSR when the Pal on the card is meta-relevant in tournament decks.",
  },
  {
    q: "Are alternate art Palworld cards tournament legal?",
    a: "Yes. A parallel printing is the same card as its base number, so it is legal anywhere the base card is legal and counts towards the same 4-copy limit.",
  },
  {
    q: "Do alternate art prices update as often as base cards?",
    a: "Yes — every tracked printing, parallel or base, is repriced on the same twice-daily sync. No printing waits in a weekly refresh rotation, which matters most for chase parallels because those are the cards that spike.",
  },
];

/** Parallel tiers, most valuable first — each becomes its own bounded table. */
const TIERS: Array<{ key: string; label: string; codes: string[] }> = [
  { key: "OSR", label: "OSR — Ultra Secret Gold parallels", codes: ["OSR"] },
  { key: "SSP", label: "SSP — Special Signature Parallels", codes: ["SSP"] },
  { key: "TSP", label: "TSP / TSR — Trial Deck secret parallels", codes: ["TSP", "TSR"] },
  { key: "SR", label: "SR / SP — secret and special parallels", codes: ["SR", "SP"] },
];

export const Route = createFileRoute("/palworld-alternate-art-price-guide")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["price-board"],
      queryFn: () => getPriceBoard(),
      staleTime: 5 * 60 * 1000,
    }),
  head: () => ({
    meta: [
      { title: pageTitle("Palworld TCG Alternate Art Price Guide") },
      {
        name: "description",
        content:
          "Palworld alternate art price guide: OSR, SSP, SR/SP and Trial Deck secret parallels priced by rarity tier, with TCGplayer sold averages and 7-day moves, updated daily.",
      },
      { property: "og:title", content: "Palworld TCG Alternate Art Price Guide" },
      {
        property: "og:description",
        content: "What every Palworld parallel printing is worth, grouped by rarity tier and rebuilt on each daily sync.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["TCG Price Chart", "/prices"],
        ["Alternate Art Price Guide", "/palworld-alternate-art-price-guide"],
      ]),
      datasetSchema({
        name: "Palworld TCG alternate art (parallel) card prices",
        description:
          "Market values and TCGplayer completed-sale averages for every Palworld parallel printing, grouped by rarity tier.",
        path: "/palworld-alternate-art-price-guide",
        keywords: [
          "Palworld alternate art price guide",
          "Palworld SSP card price",
          "Palworld OSR card value",
          "Palworld parallel card prices",
          "Palworld TCG secret rare prices",
        ],
        variables: ["Market price (USD)", "TCGplayer sold average (USD)", "7-day change (%)", "Rarity tier"],
      }),
      snippetAnswerSchema({ question: SNIPPET_Q, answer: SNIPPET_A, path: "/palworld-alternate-art-price-guide" }),
      collectionSchema({
        name: "Palworld TCG Alternate Art Price Guide",
        description: "Directory of every Palworld OFFICIAL CARD GAME parallel printing grouped by rarity tier.",
        path: "/palworld-alternate-art-price-guide",
        items: OFFICIAL_CARDS.filter((c) => c.variant).map((c) => ({ name: `${c.code} ${c.name} alternate art price`, path: `/cards/${c.code}` })),
      }),
      faqSchema(FAQS, URL),
    ],
  }),
  component: AlternateArtPage,
});

function AlternateArtPage() {
  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
  });

  const parallels = useMemo(
    () => (data?.rows ?? []).filter((r) => OFFICIAL_CARD_BY_CODE.get(r.code.toUpperCase())?.variant),
    [data?.rows],
  );

  const tiers = useMemo(
    () =>
      TIERS.map((t) => ({
        ...t,
        rows: parallels
          .filter((r) => t.codes.includes(r.rarity as string))
          .sort((a: PriceRow, b: PriceRow) => b.market - a.market)
          .slice(0, 15),
      })).filter((t) => t.rows.length > 0),
    [parallels],
  );

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-primary">Home</Link> ·{" "}
        <Link to="/prices" className="hover:text-primary">TCG Price Chart</Link> ·{" "}
        <span>Alternate Art Price Guide</span>
      </nav>

      <header>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <h1 className="text-3xl font-extrabold sm:text-4xl">Palworld TCG Alternate Art Price Guide</h1>
          <ShareButton
            title="Palworld TCG Alternate Art Price Guide — Pal Stars"
            text="Parallel printings grouped by rarity tier with live market values and TCGplayer sold averages."
            path="/palworld-alternate-art-price-guide"
          />
        </div>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Parallel printings only, separated from the base cards and grouped by rarity tier so each price band reads as
          its own short table. A parallel shares its base card's name, number and rules text, so everything you pay above
          the base price is scarcity.
        </p>
      </header>

      <PriceFreshness updated={data?.updated ?? null} />

      <SnippetFunnel
        className="mt-6"
        question={SNIPPET_Q}
        answer={SNIPPET_A}
        withheld={[
          "Current market value of every OSR and SSP printing",
          "TCGplayer completed-sale average per parallel",
          "Price band per rarity tier, ranked",
          "7-day move on each chase parallel",
        ]}
        cta={{ to: "/most-expensive-palworld-cards", label: "See the most expensive Palworld cards" }}
      />

      {tiers.map((t) => (
        <div key={t.key}>
          <PriceTable
            id={`tier-${t.key.toLowerCase()}`}
            heading={t.label}
            caption={`Palworld ${t.key} parallel printings ranked by market value, with TCGplayer sold averages`}
            rows={t.rows}
          />
          {RARITY_MEANINGS[t.codes[0] as keyof typeof RARITY_MEANINGS] ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {RARITY_MEANINGS[t.codes[0] as keyof typeof RARITY_MEANINGS]}
            </p>
          ) : null}
        </div>
      ))}

      <p className="mt-6 text-sm text-muted-foreground">
        Completed sales:{" "}
        <Link to="/tcg-sold-prices" className="text-primary hover:underline">
          Palworld TCG sold prices
        </Link>{" "}
        · Full board:{" "}
        <Link to="/prices" className="text-primary hover:underline">
          Palworld TCG price chart
        </Link>{" "}
        · Buy a list in one order:{" "}
        <Link to="/tcg-bulk-order" className="text-primary hover:underline">
          Palworld TCG bulk order
        </Link>
        .
      </p>

      <div className="mt-6">
        <AffiliateDisclosure />
      </div>

      <FaqSection items={FAQS} />
      <CrossCategoryLinks side="tcg" />
    </main>
  );
}
